import React, { useEffect, useState } from "react";
import {
  Typography, Box, CircularProgress, Alert, Container,
  TextField, InputAdornment, Pagination,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { Search } from "@mui/icons-material";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import config from "../../config";
import { connect } from "react-redux";
import * as Actions from "../../redux/actions/";

/* ── Store card ─────────────────────────── */
const StoreCard = ({ item, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, ease: "easeOut", delay: (index % 5) * 0.05 }}
  >
    <Box
      component={Link}
      to={"/neighbour/" + item.id}
      sx={{ textDecoration: "none", display: "block" }}
    >
      <Box
        sx={{
          backgroundColor: "#1a1a1a",
          border: "1px solid #2a2a2a",
          borderRadius: "14px",
          overflow: "hidden",
          transition: "all 0.25s ease",
          "&:hover": {
            border: "1px solid #FFC001",
            transform: "translateY(-4px)",
            boxShadow: "0 12px 32px rgba(255,192,1,0.1)",
            "& .store-name": { color: "#FFC001" },
          },
        }}
      >
        {/* Logo area */}
        <Box
          sx={{
            height: 180,
            backgroundColor: "#111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            borderBottom: "1px solid #222",
          }}
        >
          {item.brand_logo?.url ? (
            <img
              src={item.brand_logo.url}
              alt={item.brand_name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <StorefrontIcon sx={{ color: "#333", fontSize: 56 }} />
          )}
        </Box>

        {/* Info */}
        <Box sx={{ p: 2 }}>
          <Typography
            className="store-name"
            noWrap
            sx={{ color: "#fff", fontWeight: 700, fontSize: "15px", mb: 0.5, transition: "color 0.2s" }}
          >
            {item.brand_name}
          </Typography>

          {item.location && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
              <FmdGoodIcon sx={{ color: "#555", fontSize: 13 }} />
              <Typography noWrap sx={{ color: "#777", fontSize: "12px" }}>{item.location}</Typography>
            </Box>
          )}

          {item.delivery_option && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {item.delivery_option.includes("regional_pickup")
                ? <LocalShippingIcon sx={{ color: "#555", fontSize: 13 }} />
                : <DirectionsCarIcon sx={{ color: "#555", fontSize: 13 }} />}
              <Typography sx={{ color: "#777", fontSize: "12px" }}>
                {item.delivery_option.includes("regional_pickup") ? "Regional Pickup" : "Local Pickup"}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  </motion.div>
);

/* ══════════════════════════════════════════ */
const Stores = ({ jwtToken }) => {
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 20;
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    const getStores = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${config.SERVER_URL}/api/v1/all_stores?per_page=${rowsPerPage}&page=${page}`,
          { headers: { Authorization: `Bearer ${jwtToken}` } }
        );
        if (res.status === 200) {
          setStores(res.data.data);
          setTotalRecords(res.data.total_stores);
        }
      } catch {
        setSnackbar({ open: true, message: "Unable to load stores right now." });
      }
      setLoading(false);
    };
    getStores();
  }, [page, jwtToken]);

  const filtered = stores.filter((s) =>
    search === "" || s.brand_name?.toLowerCase().includes(search.toLowerCase()) || s.location?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled" severity="error">{snackbar.message}</Alert>
      </Snackbar>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

        {/* ── Hero header ── */}
        <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 5, md: 7 }, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Stores</Typography>
            </Box>

            {/* Title */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "48px" }, color: "#fff", lineHeight: 1.1, mb: 1 }}>
                Moore Exotics{" "}
                <Box component="span" sx={{ color: "#FFC001" }}>Neighbours</Box>
              </Typography>
              <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" } }}>
                Browse trusted exotic animal stores and breeders
              </Typography>
            </Box>

            {/* Search */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <TextField
                placeholder="Search stores or locations..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "460px" },
                  backgroundColor: "#1a1a1a",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    paddingRight: 0,
                    color: "#fff",
                    borderRadius: "10px",
                    "& fieldset": { borderColor: "#333" },
                    "&:hover fieldset": { borderColor: "#FFC001" },
                    "&.Mui-focused fieldset": { borderColor: "#FFC001" },
                  },
                  "& .MuiInputBase-input::placeholder": { color: "#666", opacity: 1 },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ backgroundColor: "#FFC001", height: "40px", width: "44px", display: "flex", alignItems: "center", justifyContent: "center", borderTopRightRadius: "10px", borderBottomRightRadius: "10px" }}>
                        <Search sx={{ color: "#000", fontSize: 20 }} />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Container>
        </Box>

        {/* ── Grid ── */}
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#FFC001" }} />
            </Box>
          ) : filtered.length > 0 ? (
            <>
              <Typography sx={{ color: "#555", fontSize: "13px", mb: 2 }}>
                {totalRecords} store{totalRecords !== 1 ? "s" : ""}
              </Typography>
              <Box sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
              }}>
                {filtered.map((item, i) => (
                  <StoreCard key={item.id} item={item} index={i} />
                ))}
              </Box>

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, val) => { setPage(val); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    sx={{
                      "& .MuiPaginationItem-root": { color: "#777", borderColor: "#333" },
                      "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#FFC001", color: "#000", fontWeight: 700 },
                      "& .MuiPaginationItem-root:hover": { borderColor: "#FFC001", color: "#FFC001" },
                    }}
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2 }}>
              <StorefrontIcon sx={{ color: "#333", fontSize: 56 }} />
              <Typography sx={{ color: "#555", fontSize: "15px" }}>No stores found</Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = ({ Auth, Seller, Store }) => ({
  jwtToken: Auth.jwtToken,
  seller: Seller.seller,
  storeId: Store.store,
});

const mapDispatchToProps = (dispatch) => ({
  setStore: (data) => dispatch(Actions.setStore(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Stores);
